import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const returnSchema = z.object({
  reason: z.string().optional(),
  return_date: z.string().optional(),
  refund_method: z.string().default('cash'),
});

type ReturnFormData = z.infer<typeof returnSchema>;

interface Sale {
  id: string;
  invoice_number: number;
  total_amount: number;
  subtotal?: number;
  discount_type?: 'percentage' | 'fixed' | null;
  discount_value?: number;
  created_at: string;
  items?: SaleItem[];
  customer?: {
    name?: string | null;
    phone?: string | null;
    address?: string | null;
  };
}

interface SaleItem {
  id: string;
  sale_id: string;
  item_id: string;
  quantity: number;
  price: number;
}

interface ReturnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sale: Sale | null;
  onSubmit: (data: any) => void;
  currencySymbol: string;
  // Optional map of item_id -> item name for proper display
  itemNameMap?: Record<string, string>;
  // Store's configured timezone, so the default return date/time reflects
  // the business's local time rather than the viewer's browser/UTC clock.
  systemTimezone?: string;
}

interface ReturnItem {
  sale_item_id: string;
  item_id: string;
  item_name: string;
  original_quantity: number;
  quantity_returned: number;
  original_price: number;
  return_amount: number;
  selected: boolean;
  unit_refund_price?: number | null; // allow custom per-item refund price
}

export default function ReturnDialog({
  open,
  onOpenChange,
  sale,
  onSubmit,
  currencySymbol,
  itemNameMap,
  systemTimezone = 'Africa/Windhoek',
}: ReturnDialogProps) {
  const [returnItems, setReturnItems] = useState<ReturnItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [customRefundAmount, setCustomRefundAmount] = useState<number>(0);
  const [allowCustomPerItemRefund, setAllowCustomPerItemRefund] = useState<boolean>(false);

  const form = useForm<ReturnFormData>({
    resolver: zodResolver(returnSchema),
    defaultValues: {
      reason: '',
      return_date: formatInTimeZone(new Date(), systemTimezone, "yyyy-MM-dd'T'HH:mm"),
    },
  });

  useEffect(() => {
    if (sale && sale.items) {
      // Calculate discount factor for proportional return amounts
      const subtotal = sale.subtotal || sale.total_amount;
      const totalAmount = sale.total_amount;
      const discountFactor = totalAmount / subtotal; // This will be < 1 if there's a discount
      
      // Initialize return items based on sale items
      const items: ReturnItem[] = sale.items.map(item => {
        // Calculate the actual price paid per item after discount
        const actualPricePaid = item.price * discountFactor;
        
        return {
          sale_item_id: item.id,
          item_id: item.item_id,
          item_name: itemNameMap?.[item.item_id] || 'Custom Item',
          original_quantity: item.quantity,
          quantity_returned: 0,
          original_price: actualPricePaid, // Use actual price paid after discount
          return_amount: 0,
          selected: false,
          unit_refund_price: null,
        };
      });
      setReturnItems(items);
      setCustomRefundAmount(0);
    }
  }, [sale, itemNameMap]);

  // Recalculate amounts when toggling custom per-item refund
  useEffect(() => {
    setReturnItems(prev => prev.map(item => {
      const unit = allowCustomPerItemRefund && item.unit_refund_price != null
        ? Math.min(Math.max(0, item.unit_refund_price || 0), item.original_price)
        : item.original_price;
      return {
        ...item,
        return_amount: (item.quantity_returned || 0) * unit,
      };
    }));
    if (allowCustomPerItemRefund) {
      setCustomRefundAmount(0);
    }
  }, [allowCustomPerItemRefund]);

  const handleQuantityChange = (index: number, quantity: number) => {
    setReturnItems(prev => {
      const updated = [...prev];
      const item = updated[index];
      const clampedQuantity = Math.max(0, Math.min(quantity, item.original_quantity));
      const effectiveUnit = allowCustomPerItemRefund && item.unit_refund_price != null
        ? Math.min(Math.max(0, item.unit_refund_price || 0), item.original_price)
        : item.original_price;
      
      updated[index] = {
        ...item,
        quantity_returned: clampedQuantity,
        return_amount: clampedQuantity * effectiveUnit,
        selected: clampedQuantity > 0,
      };
      
      return updated;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    setReturnItems(prev => prev.map(item => ({
      ...item,
      quantity_returned: checked ? item.original_quantity : 0,
      return_amount: checked 
        ? item.original_quantity * (
            allowCustomPerItemRefund && item.unit_refund_price != null
              ? Math.min(Math.max(0, item.unit_refund_price || 0), item.original_price)
              : item.original_price
          )
        : 0,
      selected: checked,
    })));
  };

  const selectedItems = returnItems.filter(item => item.selected && item.quantity_returned > 0);
  const baseReturnAmount = selectedItems.reduce((sum, item) => sum + item.return_amount, 0);
  const totalReturnAmount = Math.max(0, baseReturnAmount + (allowCustomPerItemRefund ? 0 : (customRefundAmount || 0)));

  const handleSubmit = async (data: ReturnFormData) => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item to return');
      return;
    }

    setLoading(true);
    try {
      const returnData: any = {
        sale_id: sale?.id,
        total_return_amount: totalReturnAmount,
        reason: data.reason || null,
        return_date: data.return_date || new Date().toISOString(),
        refund_method: data.refund_method,
        items: selectedItems.map(item => ({
          sale_item_id: item.sale_item_id,
          quantity_returned: item.quantity_returned,
          return_amount: item.return_amount,
        })),
      };

      if (!allowCustomPerItemRefund) {
        returnData.custom_refund_amount = customRefundAmount || 0;
      } else {
        returnData.custom_refund_amount = 0;
      }

      await onSubmit(returnData);
      
      // Reset form
      form.reset();
      setReturnItems([]);
      onOpenChange(false);
      
      toast.success('Return processed successfully');
    } catch (error) {
      console.error('Return failed:', error);
      toast.error('Failed to process return');
    } finally {
      setLoading(false);
    }
  };

  if (!sale) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Process Return</DialogTitle>
          <DialogDescription>
            Process return for Sale #{sale.invoice_number} - {format(new Date(sale.created_at), 'MMM dd, yyyy')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Sale Summary */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-2">Sale Summary</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Invoice #:</span> {sale.invoice_number}
              </div>
              <div>
                <span className="text-muted-foreground">Total Amount:</span> {currencySymbol}{sale.total_amount.toFixed(2)}
              </div>
              <div>
                <span className="text-muted-foreground">Date:</span> {format(new Date(sale.created_at), 'MMM dd, yyyy HH:mm')}
              </div>
              <div>
                <span className="text-muted-foreground">Items:</span> {sale.items?.length || 0}
              </div>
              {(sale.discount_value ?? 0) > 0 && (
                <>
                  <div>
                    <span className="text-muted-foreground">Subtotal:</span> {currencySymbol}{(sale.subtotal || sale.total_amount).toFixed(2)}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Discount:</span> 
                    <span className="text-green-600 ml-1">
                      {sale.discount_type === 'percentage' ? `${sale.discount_value}%` : `${currencySymbol}${sale.discount_value.toFixed(2)}`}
                    </span>
                  </div>
                </>
              )}
            </div>
            {(sale.discount_value ?? 0) > 0 && (
              <div className="mt-2 p-2 bg-blue-50 rounded text-sm text-blue-700">
                <strong>Note:</strong> Return amounts are calculated based on the discounted price actually paid.
              </div>
            )}
          </div>

          {/* Return Items Table */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">Select Items to Return</h3>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="select-all"
                  checked={returnItems.length > 0 && returnItems.every(item => item.selected)}
                  onCheckedChange={handleSelectAll}
                />
                <label htmlFor="select-all" className="text-sm">Select All</label>
              </div>
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Checkbox id="custom-per-item" checked={allowCustomPerItemRefund} onCheckedChange={(c) => setAllowCustomPerItemRefund(!!c)} />
                <label htmlFor="custom-per-item" className="text-sm">Enable custom per-item refund</label>
              </div>
              {allowCustomPerItemRefund && (
                <div className="text-xs text-muted-foreground">Tip: Set a unit refund price lower than the original to refund less.</div>
              )}
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Select</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Original Qty</TableHead>
                  <TableHead>Return Qty</TableHead>
                  <TableHead>Unit Refund Price</TableHead>
                  <TableHead>Return Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {returnItems.map((item, index) => (
                  <TableRow key={item.sale_item_id}>
                    <TableCell>
                      <Checkbox
                        checked={item.selected}
                        onCheckedChange={(checked) => {
                          setReturnItems(prev => {
                            const updated = [...prev];
                            updated[index] = {
                              ...updated[index],
                              selected: !!checked,
                              quantity_returned: checked ? item.original_quantity : 0,
                              return_amount: checked ? item.original_quantity * item.original_price : 0,
                            };
                            return updated;
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell>{item.item_name}</TableCell>
                    <TableCell>{item.original_quantity}</TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        max={item.original_quantity}
                        value={item.quantity_returned === 0 ? '' : item.quantity_returned}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleQuantityChange(index, value === '' ? 0 : (parseInt(value, 10) || 0));
                        }}
                        className="w-20"
                        disabled={!item.selected}
                      />
                    </TableCell>
                    <TableCell>
                      {allowCustomPerItemRefund && item.selected ? (
                        <Input
                          type="number"
                          min={0}
                          max={item.original_price}
                          step="0.01"
                          value={item.unit_refund_price ?? item.original_price}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            setReturnItems(prev => {
                              const updated = [...prev];
                              const safe = isNaN(val) ? 0 : Math.min(Math.max(0, val), item.original_price);
                              const qty = updated[index].quantity_returned;
                              updated[index] = {
                                ...updated[index],
                                unit_refund_price: safe,
                                return_amount: qty * safe,
                              };
                              return updated;
                            });
                          }}
                          className="w-24"
                        />
                      ) : (
                        <span>{currencySymbol}{item.original_price.toFixed(2)}</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {currencySymbol}{item.return_amount.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Custom Refund Amount and Total */}
            <div className="flex justify-end mt-4">
              <div className="text-right space-y-2">
                {!allowCustomPerItemRefund && (
                  <div>
                    <label className="text-sm font-medium mr-2">Custom Refund Amount</label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={customRefundAmount === 0 ? '' : customRefundAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCustomRefundAmount(value === '' ? 0 : Math.max(0, parseFloat(value) || 0));
                      }}
                      className="w-40 inline-block ml-2"
                      placeholder={`${currencySymbol}0.00`}
                    />
                  </div>
                )}
                <div className="text-lg font-semibold">
                  Total Return Amount: {currencySymbol}{totalReturnAmount.toFixed(2)}
                </div>
                <div className="text-sm text-muted-foreground">
                  {selectedItems.length} item(s) selected for return
                </div>
              </div>
            </div>
          </div>

          {/* Return Form */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Return Reason (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter reason for return..."
                        {...field}
                        rows={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="refund_method"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Refund Method</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select refund method" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="credit_card">Credit Card</SelectItem>
                        <SelectItem value="debit_card">Debit Card</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                        <SelectItem value="credit_note">Credit Note</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="return_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Return Date</FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading || selectedItems.length === 0}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {loading ? 'Processing...' : 'Process Return'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
